/**
 * MER Dashboard — Design Tokens
 *
 * Single source of truth for color, spacing, type, radius, shadow, motion.
 * All sizes are in `rem`. The app sets `html { font-size: 62.5% }` in
 * index.scss, so 1rem === 10px. Multiply rem values by 10 to get px.
 *
 * Convention: colors avoid pure neutrals — slate scales carry a subtle
 * cool/blue undertone for a more refined look (Linear/Vercel-style).
 */

// Append an alpha channel to a hex color. `withAlpha('#22c55e', 0.12)` → '#22c55e1f'
export const withAlpha = (hex, a) => {
  const clamped = Math.max(0, Math.min(1, a));
  const aHex = Math.round(clamped * 255).toString(16).padStart(2, '0');
  return hex + aHex;
};

// Color palette. Dark surfaces are tuned for overlay/modal contexts;
// the broader app remains light (Bootstrap/AntD).
export const colors = {
  // Dark surfaces (used by the InstantPanel and other overlay UI)
  dark: {
    bg:           '#0b0d12',  // panel background — near-black with cool undertone
    surface:      '#151821',  // raised cards on top of bg
    surfaceHi:    '#1d2130',  // hover / elevated cells
    border:       '#262a37',
    borderStrong: '#373b4c',
    text:         '#e7ecf3',
    textMuted:    '#8a94a6',
    textDim:      '#5b6478',
  },

  // Light surfaces (placeholder for future light-themed redesigns)
  light: {
    bg:           '#ffffff',
    surface:      '#f7f8fa',
    surfaceHi:    '#eef0f4',
    border:       '#e4e7ec',
    borderStrong: '#d0d5dd',
    text:         '#0f1729',
    textMuted:    '#475467',
    textDim:      '#98a2b3',
  },

  // Brand & semantic. `Hi` variant is for hover/active states.
  primary:    '#8852cc',
  primaryHi:  '#9a67d8',
  primaryLo:  '#6e3eaa',
  success:    '#22c55e',
  successHi:  '#34d670',
  warning:    '#f59e0b',
  warningHi:  '#fbbf24',
  danger:     '#ef4444',
  dangerHi:   '#f56565',
  info:       '#3b82f6',

  // Pure neutrals for occasional use
  white: '#ffffff',
  black: '#000000',
};

// 4px grid (0.4rem under 62.5% root). Stick to these — no one-off magic numbers.
export const space = {
  px: '1px',
  0:  '0',
  1:  '0.4rem',  // 4
  2:  '0.8rem',  // 8
  3:  '1.2rem',  // 12
  4:  '1.6rem',  // 16
  5:  '2rem',    // 20
  6:  '2.4rem',  // 24
  7:  '3.2rem',  // 32
  8:  '4rem',    // 40
  9:  '4.8rem',  // 48
  10: '6.4rem',  // 64
};

export const radius = {
  xs:   '0.4rem',  // 4
  sm:   '0.6rem',  // 6
  md:   '0.8rem',  // 8
  lg:   '1.2rem',  // 12
  xl:   '1.6rem',  // 16
  pill: '9999px',
};

// Type scale (rem === px / 10)
export const fontSize = {
  xs:   '1.1rem',
  sm:   '1.2rem',
  md:   '1.3rem',
  base: '1.4rem',
  lg:   '1.6rem',
  xl:   '1.8rem',
  '2xl':'2rem',
  '3xl':'2.4rem',
  '4xl':'3.2rem',
};

export const fontWeight = {
  regular:  400,
  medium:   500,
  semibold: 600,
  bold:     700,
};

export const lineHeight = {
  tight:   1.15,  // headings
  snug:    1.3,
  normal:  1.5,   // body
  relaxed: 1.65,
};

export const fontFamily = {
  // System stack first — Inter/Geist preferred when available locally.
  // Brandon (the existing brand font) is intentionally NOT here so the
  // refreshed UI reads as a distinct, neutral system rather than the legacy theme.
  sans: `'Inter', 'Geist', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif`,
  mono: `ui-monospace, 'SF Mono', 'JetBrains Mono', 'Roboto Mono', Menlo, Consolas, monospace`,
};

// Layered shadows — multiple low-opacity drops compose more realistic depth
// than a single harsh drop. `modal` is sized for full-page overlays.
export const shadow = {
  xs:    '0 1px 1px rgba(0,0,0,0.05)',
  sm:    '0 1px 2px rgba(0,0,0,0.08), 0 1px 3px rgba(0,0,0,0.06)',
  md:    '0 4px 6px rgba(0,0,0,0.06), 0 2px 4px rgba(0,0,0,0.04)',
  lg:    '0 10px 15px rgba(0,0,0,0.10), 0 4px 6px rgba(0,0,0,0.05)',
  xl:    '0 20px 30px rgba(0,0,0,0.18), 0 8px 12px rgba(0,0,0,0.10)',
  modal: '0 24px 48px -12px rgba(0,0,0,0.55), 0 12px 24px -8px rgba(0,0,0,0.40)',
};

// `focusRing(color)` returns a box-shadow string for use in :focus-visible.
export const focusRing = (color, alpha = 0.35) =>
  `0 0 0 3px ${withAlpha(color, alpha)}`;

// Motion — sharp Linear/Vercel-style easing. Keep durations tight (≤260ms).
export const motion = {
  fast: '120ms cubic-bezier(0.4, 0, 0.2, 1)',
  base: '180ms cubic-bezier(0.4, 0, 0.2, 1)',
  slow: '260ms cubic-bezier(0.4, 0, 0.2, 1)',
};

export const z = {
  base:     1,
  raised:   10,
  sticky:   100,
  dropdown: 1000,
  overlay:  9000,
  modal:    9100,
  toast:    9500,
};

const tokens = {
  colors,
  space,
  radius,
  fontSize,
  fontWeight,
  lineHeight,
  fontFamily,
  shadow,
  focusRing,
  motion,
  z,
  withAlpha,
};

export default tokens;
